import { S3_BRAND_MEDIA_BUCKET_LINK } from 'constants/links';
import { DEFAULT_BRAND_FONTS } from 'modules/Editor/components/AutoBrand/AutoBrand.config';
import { BrandAssets, BrandState } from 'modules/User/brand.types';

import { THUMBNAIL_HEIGHT } from '../InfiniteGrid/InfiniteGrid.config';

export interface SerializeData {
  previewHeight: number;
  tags: string;
  previewURL: string;
}

interface SerializeLogoDataProps {
  data: string[];
}

export interface SerializeAssetsDataProps {
  data?: BrandAssets[];
  filter?: BrandAssets['type'];
}

/**
 * Standardize the logo data from brand to a data format that can be used by the react-window variable grid
 * to correctly render a cell in the grid,
 * we will need the tags for a image, a url of the image, and the height of the image
 *
 * @param {object} props
 * @param {string[]} props.data a list of file names from the E1 end-point
 * @returns a array
 */
export const serializeLogoData = ({ data }: SerializeLogoDataProps): SerializeData[] => {
  return data.map((fileName) => ({
    previewHeight: THUMBNAIL_HEIGHT,
    tags: fileName,
    previewURL: `${S3_BRAND_MEDIA_BUCKET_LINK}/${fileName}`,
  }));
};

/**
 * Standardizes the assets data from the brand into a format that can be used
 * by the react-window variable grid to correctly render a cell in the grid.
 * Filters the assets based on the specified type and maps them to include
 * the tags for the asset, a URL of the asset, and the height of the asset.
 *
 * @param {object} props
 * @param {BrandAssets[]} props.data - A list of brand assets.
 * @param {'images' | 'icons'} [props.filter='images'] - The type of assets to filter by.
 * @returns An array of standardized asset objects.
 */

export const serializeAssetsData = ({ data = [], filter = 'images' }: SerializeAssetsDataProps): SerializeData[] => {
  return data
    ?.filter((assets) => assets.type === filter)
    .map((assets) => ({
      previewHeight: THUMBNAIL_HEIGHT,
      tags: assets.file,
      previewURL: `${S3_BRAND_MEDIA_BUCKET_LINK}/${assets.file}`,
    }));
};

/**
 * Get color palettes from the brand state object.
 *
 * @param brand The BrandState object that we wsant to get the color palettes.
 * @returns Two dimensional array of color string.
 */
export const getBrandColorPalette = (brand: BrandState) => {
  if (!brand?.colors) {
    return [];
  }

  return brand.colors.map((item) => item.map((childItem) => childItem.color));
};

/**
 * Check if the given font names are the default font names.
 *
 * @param title The title font name.
 * @param subtitle The subtitle font name.
 * @param text The text font name.
 *
 * @returns {boolean} true if the given font names are default, false otherwise.
 */
export const isDefaultBrand = (title?: string, subtitle?: string, text?: string): boolean => {
  if (
    DEFAULT_BRAND_FONTS.title.fontFamily === title &&
    DEFAULT_BRAND_FONTS.subtitle.fontFamily === subtitle &&
    DEFAULT_BRAND_FONTS.text.fontFamily === text
  ) {
    return true;
  }

  return false;
};
